import React, { useState } from "react";
import { FaPlay } from "react-icons/fa";
import { motion, useAnimation, useInView } from "framer-motion";
import { useEffect, useRef } from "react";

const Counter = ({ from = 0, to, label }) => {
    const controls = useAnimation();
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true });

    useEffect(() => {
        if (isInView) {
            controls.start({ count: to });
        }
    }, [isInView, to, controls]);

    return (
        <motion.div
            ref={ref}
            className="text-center space-y-2"
            initial={{ count: from }}
            animate={controls}
        >
            <motion.h3
                className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-gray-900 to-gray-800 bg-clip-text text-transparent"
                transition={{ duration: 2, ease: "easeOut" }}
                animate={{ opacity: 1 }}
            >
                <motion.span>
                    {to.toString().replace(/\D/g, "")}
                    {to.toString().replace(/[\d]/g, "").slice(-1)}
                </motion.span>
            </motion.h3>
            <p className="text-gray-600 font-medium">{label}</p>
        </motion.div>
    );
};

export default function OurVision() {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <section className="bg-[#0f0f0f] text-white py-24 px-6 md:px-20 flex flex-col items-center">
            {/* Top Layout */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-12 max-w-7xl w-full">
                {/* Video Preview */}
                <div
                    onClick={() => setIsOpen(true)}
                    className="relative cursor-pointer rounded-2xl overflow-hidden shadow-xl w-full md:w-1/2 group transition-transform duration-300 hover:scale-105"
                >
                    <img
                        src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=800&q=80"
                        alt="Team session"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="bg-white p-4 rounded-full shadow-lg transition transform group-hover:scale-110">
                            <FaPlay className="text-black text-xl" />
                        </div>
                    </div>
                </div>

                {/* Mission Text */}
                <div className="text-left w-full md:w-1/2 space-y-4">
                    <h2 className="text-4xl md:text-5xl font-extrabold leading-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-lime-400 to-blue-500">
              Our mission is to
              <br />
              empower businesses
            </span>
                    </h2>
                    <p className="text-gray-300 text-lg">
                        through innovative solutions and exceptional service.
                    </p>
                </div>
            </div>

            {/* Stats */}
            <div className="mt-20 bg-white text-black px-10 py-10 rounded-2xl shadow-2xl grid grid-cols-1 md:grid-cols-3 gap-10 w-full max-w-5xl">
                <Counter to="95%" label="Customer Satisfaction Rate" />
                <Counter to="50%" label="Average Increase" />
                <Counter to="30+" label="Industries Served" />
            </div>
            <div className="mt-16 w-full text-center space-y-8">
                <h3 className="text-2xl font-bold text-white">Trusted by top companies</h3>
                <div className="flex flex-wrap justify-center gap-10 items-center">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg" className="h-8" alt="Apple" />
                    <img src="./core-images/assiganto-picto.png" className="w-10 h-10" alt="ASSIGANTO"/>
                    <img src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg" className="h-8" alt="Microsoft" />
                    <img src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" className="h-8" alt="Google" />
                </div>
            </div>

            {/* Modal Video */}
            {isOpen && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center px-4">
                    <div className="relative bg-black rounded-xl overflow-hidden w-full max-w-4xl shadow-2xl">
                        <button
                            onClick={() => setIsOpen(false)}
                            className="absolute top-2 right-3 text-white text-3xl font-bold z-10 hover:text-red-400"
                            aria-label="Close"
                        >
                            &times;
                        </button>
                        <div className="aspect-video w-full">
                            <iframe
                                src="https://www.youtube.com/embed/tgbNymZ7vqY"
                                title="Team Video"
                                className="w-full h-full rounded-b-xl"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
